/**
 * What an offline till keeps on the device: the menu and tables it last saw,
 * its receipt numbers, and the sales waiting to be sent.
 *
 * IndexedDB rather than localStorage: a busy evening's sales and a full menu
 * are more than localStorage should hold, and IndexedDB survives a reload and
 * a power cut the same way. One object store of keyed values is all it needs.
 *
 * The numbers are the part that must not go wrong. They come off the site's
 * own receipt series in blocks (`fiscal.offline`), and are used strictly in
 * order, block after block, so the series has no holes once the sales are
 * sent. `takeNumber` hands out the next one and writes the new position
 * before the receipt prints: a number printed and then forgotten by a crash
 * would be printed twice.
 */

import type { Menu, OfflineSale } from '@restaurant/api-client';

const DB = 'restaurant-offline';
const STORE = 'kv';

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB would not open'));
  });
}

async function get<T>(key: string): Promise<T | undefined> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const request = db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
    request.onsuccess = () => resolve(request.result as T | undefined);
    request.onerror = () => reject(request.error ?? new Error(`Could not read ${key}`));
  });
}

async function set(key: string, value: unknown): Promise<void> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error(`Could not write ${key}`));
  });
}

// --------------------------------------------------------------------------- //
// The snapshot: what the till needs to sell with no connection
// --------------------------------------------------------------------------- //

export interface Snapshot {
  savedAt: string;
  location: { id: string; name: string; currency: string; locale: string };
  /** This tablet's till, or null when it was never set: then it cannot sell offline. */
  terminalId: string | null;
  /** The plan includes offline selling (`orders.offline`). */
  entitled: boolean;
  menu: Menu;
  tables: { id: string; label: string }[];
}

export const loadSnapshot = () => get<Snapshot>('snapshot');
export const saveSnapshot = (snapshot: Snapshot) => set('snapshot', snapshot);

// --------------------------------------------------------------------------- //
// Receipt numbers
// --------------------------------------------------------------------------- //

export interface Numbers {
  terminalId: string;
  /** Blocks handed to this till, in the order they are to be used. */
  ranges: { first: number; last: number }[];
  /** The next number to print, or null before the first block arrives. */
  next: number | null;
}

export const loadNumbers = () => get<Numbers>('numbers');
export const saveNumbers = (numbers: Numbers) => set('numbers', numbers);

/** How many numbers are left in hand, from `next` on. */
export function numbersLeft(numbers: Numbers | undefined): number {
  if (!numbers || numbers.next === null) return 0;
  const next = numbers.next;
  return numbers.ranges.reduce((left, range) => {
    if (range.last < next) return left;
    return left + range.last - Math.max(range.first, next) + 1;
  }, 0);
}

/** Add a newly reserved block after the ones already held. */
export function withBlock(
  numbers: Numbers | undefined,
  terminalId: string,
  block: { first_number: number; last_number: number },
): Numbers {
  const current =
    numbers && numbers.terminalId === terminalId
      ? numbers
      : { terminalId, ranges: [], next: null };
  const ranges = [
    ...current.ranges,
    { first: block.first_number, last: block.last_number },
  ].sort((a, b) => a.first - b.first);
  return { ...current, ranges, next: current.next ?? block.first_number };
}

/** The number for this receipt, and where the till stands after it; null when none are left. */
export function nextNumber(numbers: Numbers): { number: number; after: Numbers } | null {
  if (numbers.next === null) return null;
  const at = numbers.ranges.find((r) => r.last >= numbers.next! && r.first <= numbers.next!)
    ? numbers.next
    : (numbers.ranges.find((r) => r.first > numbers.next!)?.first ?? null);
  if (at === null) return null;
  const range = numbers.ranges.find((r) => r.first <= at && r.last >= at)!;
  const following =
    at < range.last
      ? at + 1
      : (numbers.ranges.find((r) => r.first > range.last)?.first ?? at + 1);
  return { number: at, after: { ...numbers, next: following } };
}

/** Take the next number and write the new position before anything prints. */
export async function takeNumber(): Promise<number | null> {
  const numbers = await loadNumbers();
  if (!numbers) return null;
  const taken = nextNumber(numbers);
  if (!taken) return null;
  await saveNumbers(taken.after);
  return taken.number;
}

// --------------------------------------------------------------------------- //
// Sales waiting to be sent
// --------------------------------------------------------------------------- //

export interface QueuedSale {
  sale: OfflineSale;
  /** What the guest's receipt said, for a reprint before it is sent. */
  receipt: { reference: string; payload: Record<string, unknown>; issuedAt: string };
  /** Set when the server refused it: somebody has to look. */
  refused?: string;
}

export const loadQueue = async () => (await get<QueuedSale[]>('sales')) ?? [];
const saveQueue = (queue: QueuedSale[]) => set('sales', queue);

export async function enqueue(sale: QueuedSale): Promise<void> {
  const queue = await loadQueue();
  await saveQueue([...queue, sale]);
  notify();
}

export async function dequeue(clientId: string): Promise<void> {
  const queue = await loadQueue();
  await saveQueue(queue.filter((q) => q.sale.client_id !== clientId));
  notify();
}

export async function markRefused(clientId: string, reason: string): Promise<void> {
  const queue = await loadQueue();
  await saveQueue(
    queue.map((q) => (q.sale.client_id === clientId ? { ...q, refused: reason } : q)),
  );
  notify();
}

export async function clearRefusal(clientId: string): Promise<void> {
  const queue = await loadQueue();
  await saveQueue(
    queue.map((q) => {
      if (q.sale.client_id !== clientId) return q;
      const { refused: _gone, ...rest } = q;
      return rest;
    }),
  );
  notify();
}

// A change to the queue, for the screens showing its count.
const listeners = new Set<() => void>();
export function onQueueChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
function notify(): void {
  for (const listener of listeners) listener();
}
